/**
 * Vue I18n 国际化配置模块
 *
 * 本模块提供完整的多语言支持功能，包括：
 * - 语言切换和管理
 * - 浏览器语言检测
 * - Element UI 组件库的语言集成
 * - 类型安全的翻译 key（通过 TypeScript 类型定义）
 *
 * @module i18n
 */

import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { setStorage } from '@/utils/browserStorage'
// 此处不直接访问本地存储；统一由 locale-utils 负责检测

// 只导入默认语言的 Element UI 语言包
import elementJaLocale from 'element-ui/lib/locale/lang/ja'

// 只导入默认语言包，其他语言包将按需加载
import jaJP from './locales/ja-JP'
import { mergeIkkodateMessages } from './ikkodate-messages'

// 导入配置
import { DEFAULT_LOCALE, FALLBACK_LOCALE } from './config'

Vue.use(VueI18n)

// 语言检测与初始化逻辑统一由 locale-utils 提供
import { isLocaleSupported, getInitialLocale } from './locale-utils'

// 初始语言解析由 locale-utils 负责，避免与 store 形成循环依赖

// ============================================
// Vue I18n 实例配置
// ============================================

/**
 * 初始化时只加载默认语言包
 * 其他语言包将通过懒加载机制按需加载
 *
 * 注意：Element UI 的翻译在 el 字段下，不会与自定义翻译冲突
 * 但为了确保自定义翻译优先，Element UI 应该在前面
 */
const messages = {
  'ja-JP': {
    ...elementJaLocale,  // Element UI 翻译（在 el 字段下）
    ...mergeIkkodateMessages(jaJP, 'ja-JP'), // 自定义翻译（优先级更高）
  },
  // 其他语言包将通过 lazy-loader 动态加载
}

/**
 * 创建 VueI18n 实例
 */
const i18n = new VueI18n({
  locale: getInitialLocale(),
  fallbackLocale: FALLBACK_LOCALE,
  messages,
  // 在开发环境下也禁用翻译警告，因为我们有自己的 safe-translate 验证机制
  // 这样可以避免与 safe-translate.js 的警告重复，减少控制台噪音
  silentTranslationWarn: true,
  // 注意：silentFallbackWarn 在 vue-i18n v8.x 中不存在，已移除
})

// ============================================
// 语言切换功能
// ============================================

/**
 * 异步设置语言（支持懒加载）
 */
export async function setLocaleAsync(locale) {
  // 验证语言代码
  if (!isLocaleSupported(locale)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[i18n] 不支持的语言代码 "${locale}"，将使用默认语言: ${DEFAULT_LOCALE}`)
    }
    locale = DEFAULT_LOCALE
  }

  try {
    // 动态导入懒加载模块
    const { setLocaleWithLazyLoad } = await import('./lazy-loader')

    // 使用懒加载设置语言
    const actualLocale = await setLocaleWithLazyLoad(locale)

    setStorage('lang', actualLocale)

    return actualLocale
  } catch (error) {
    console.error('[i18n] 异步语言切换失败:', error)
    throw error
  }
}

export default i18n
